import { useEffect, useState } from 'react';
import {
    Settings,
    X,
    RotateCcw,
    Send,
    CheckCircle2,
    MessageSquare,
    Phone,
    Smartphone,
    RefreshCw
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';

export default function GroupList() {
    const navigate = useNavigate();

    // Config / Settings State
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [settings, setSettings] = useState({
        tpsmNumber: '',
        rpsmNumber: '',
    });
    const [tempSettings, setTempSettings] = useState(settings);
    const [modalDealerCode, setModalDealerCode] = useState('');
    const [modalWabaNumber, setModalWabaNumber] = useState('');
    const [groupsList, setGroupsList] = useState([]);
    const baseUrl = process.env.REACT_APP_GROUP_URL;

    const user = sessionStorage.getItem("user");
    useEffect(() => {
        if (!user || user === "null") {
            navigate('/group/login', { replace: true });
        }
    }, [user]);

    // Main Form State
    const initialFormState = {
        dealerCode: '',
        vinNumber: '',
        roNumber: '',
        businessNumber: '',
        customerNumber: '',
        serviceAdvisorNumber: '',
        serviceManagerNumber: '',
        customerCareManagerNumber: '',
    };

    const [formData, setFormData] = useState(initialFormState);
    const [submittedData, setSubmittedData] = useState(null);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [formErrors, setFormErrors] = useState({});
    const [settingsErrors, setSettingsErrors] = useState({});

    // List of field names that are mobile/phone numbers
    const phoneFields = [
        'groupNumber',
        'businessNumber',
        'customerNumber',
        'serviceAdvisorNumber',
        'serviceManagerNumber',
        'customerCareManagerNumber',
    ];

    const settingsPhoneFields = ['tpsmNumber', 'rpsmNumber'];

    // Helper to format phone input with 91 prefix automatically
    const formatPhoneInput = (val) => {
        const digits = val.replace(/[^0-9]/g, '');
        if (!digits) return '';
        if (digits.startsWith('91')) {
            return digits.slice(0, 12);
        }
        return ('91' + digits).slice(0, 12);
    };

    // Helper to format ISO date time string to human-readable local date & time
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return dateStr;
            return date.toLocaleString(undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: true,
            });
        } catch {
            return dateStr;
        }
    };

    // Helper to validate phone numbers: must have 91 prefix + 10 digits (12 total digits)
    const validatePhoneNumber = (num) => {
        if (!num || num === '91') return { valid: false, message: 'is required.' };
        const digits = num.replace(/[^0-9]/g, '');
        if (digits.length < 12) return { valid: false, message: 'must be a valid 10-digit mobile number.' };
        return { valid: true };
    };

    // Handle Main Form Input Changes (Auto-prefixes 91, Max 12 digits for phone numbers)
    const handleFormChange = (e) => {
        const { name, value } = e.target;

        let sanitizedValue = value;
        if (phoneFields.includes(name)) {
            sanitizedValue = formatPhoneInput(value);
        } else if (name === 'vinNumber') {
            sanitizedValue = value.toUpperCase().slice(0, 17);
        } else if (name === 'roNumber') {
            sanitizedValue = value.toUpperCase().slice(0, 15);
        }

        setFormData((prev) => ({ ...prev, [name]: sanitizedValue }));
        if (formErrors[name]) {
            setFormErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Handle Settings Input Changes (Auto-prefixes 91, Max 12 digits for phone numbers)
    const handleSettingsChange = (e) => {
        const { name, value } = e.target;

        const sanitizedValue = settingsPhoneFields.includes(name)
            ? formatPhoneInput(value)
            : value;

        setTempSettings((prev) => ({ ...prev, [name]: sanitizedValue }));
        if (settingsErrors[name]) {
            setSettingsErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    // Save Config Settings
    const handleSaveSettings = async (e) => {
        e.preventDefault();

        const errors = {};
        if (tempSettings.tpsmNumber) {
            const val = validatePhoneNumber(tempSettings.tpsmNumber);
            if (!val.valid) {
                errors.tpsmNumber = `TPSM Number ${val.message}`;
            }
        }
        if (tempSettings.rpsmNumber) {
            const val = validatePhoneNumber(tempSettings.rpsmNumber);
            if (!val.valid) {
                errors.rpsmNumber = `RPSM Number ${val.message}`;
            }
        }

        if (Object.keys(errors).length > 0) {
            setSettingsErrors(errors);
            toast.error(Object.values(errors)[0]);
            return;
        }

        setSettingsErrors({});

        try {
            await handleTpsm(tempSettings);
            setSettings(tempSettings);
            toast.success("Settings Saved Successfully");
        } catch (err) {
            toast.error('Unable to save TPSM/RPSM settings');
            console.error('Unable to save TPSM/RPSM settings:', err);
        } finally {
            setIsSettingsOpen(false);
        }
    };

    const updateSettingsFromPageData = (data) => {
        // If no data is provided, do not modify modal values (prevents accidental clearing)
        if (!data) return;

        // Preserve existing modal values when properties are missing
        setModalDealerCode(data?.dlrCode ?? modalDealerCode);
        setModalWabaNumber(data?.wabaNo ?? modalWabaNumber);
    };

    const mapFormDataToApiPayload = (data) => ({
        dlrCode: (modalDealerCode || ''),
        vin: data.vinNumber,
        rOnum: data.roNumber,
        wabaNo: (modalWabaNumber || ''),
        groupNos: [
            {
                name: "",
                mobileNo: data.customerNumber,
                type: "custNo"
            },
            {
                name: "",
                mobileNo: data.serviceAdvisorNumber,
                type: "saNo"
            },
            {
                name: "",
                mobileNo: data.serviceManagerNumber,
                type: "smNo"
            },
            {
                name: "",
                mobileNo: data.customerCareManagerNumber,
                type: "ccmNo"
            }
        ]
    });

    const mapApiPayloadToFormData = (payload) => ({
        dealerCode: payload.dlrCode || '',
        vinNumber: payload.vin || '',
        roNumber: payload.rOnum || '',
        businessNumber: payload.wabaNo || '',
        customerNumber: payload.custNo || '',
        serviceAdvisorNumber: payload.saNo || '',
        serviceManagerNumber: payload.smNo || '',
        customerCareManagerNumber: payload.ccmNo || '',
    });

    /**
     * Async function to save all values on the createGroup API
     * @param {Object} [customData] - Optional payload override, defaults to current formData only
     */
    const createGroup = async (customData) => {
        const payload = customData || mapFormDataToApiPayload(formData);
        const submittedValues = customData ? mapApiPayloadToFormData(customData) : formData;

        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch(`${baseUrl}WhatsappGroup/CreatewabaGrpMast`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`API error: ${response.status} ${response.statusText}`);
            }

            const result = await response.json();

            // Parse the outer "msg" string (it's a JSON string)
            let parsedMsg = null;
            try {
                parsedMsg = typeof result.msg === 'string' ? JSON.parse(result.msg) : result.msg;
            } catch {
                parsedMsg = result;
            }
            // Check for error in the parsed response
            if (parsedMsg?.error) {
                toast.error(parsedMsg.error?.message || parsedMsg.error || 'An error occurred.');
                return result;
            }
            // Parse the nested "group" string (it's also a JSON string)
            let parsedGroup = null;
            try {
                parsedGroup = typeof parsedMsg?.group === 'string'
                    ? JSON.parse(parsedMsg.group)
                    : parsedMsg?.group;
            } catch {
                parsedGroup = null;
            }
            // Show the group's message in a success toast, fallback to outer message
            const successMessage =
                parsedGroup?.message || parsedMsg?.message || 'Group created successfully.';
            toast.success(successMessage);

            setSubmittedData(submittedValues);
            setIsSuccessModalOpen(true);

            return result;
        } catch (err) {
            console.error('Error saving group details via createGroup API:', err);
            setError(err.message || 'Failed to connect to creategroup API');
        } finally {
            setIsLoading(false);
        }
    };

    const mapSettingsToApiPayload = (settingsData) => ({
        tpsMmobNo: settingsData?.tpsmNumber,
        rpsMmobNo: settingsData?.rpsmNumber,
        wabaNo: modalWabaNumber,
        dlrCode: modalDealerCode,
    });

    const mapApiPayloadToSettings = (payload) => ({
        tpsmNumber: payload?.tpsMmobNo || '',
        rpsmNumber: payload?.rpsMmobNo || '',
    });

    const normalizeSettingsResponse = (responseData) => {
        if (Array.isArray(responseData)) {
            return responseData[0] || {};
        }
        return responseData.data || {};
    };

    /**
   * Fetches or saves TPSM data.
   * Optional payload. If provided, sends a PUT request to update settings.
   * API response data
   */
    const handleTpsm = async (dataToSave = null) => {
        try {
            const isSaveOperation = Boolean(dataToSave);

            const options = {
                method: isSaveOperation ? 'POST' : 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
                ...(isSaveOperation && { body: JSON.stringify(mapSettingsToApiPayload(dataToSave)) }),
            };

            const endpoint = isSaveOperation
                ? `${baseUrl}WhatsappGroup/CreateWabaGrpDlr`
                : `${baseUrl}WhatsappGroup/GetWabaGrpdlr`;

            const response = await fetch(endpoint, options);

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }

            const result = await response.json();
            return normalizeSettingsResponse(result);
        } catch (error) {
            console.error('Error handling TPSM request:', error);
            throw error;
        }
    };

    // Submit Main Form
    const handleSubmit = async (e) => {
        e.preventDefault();

        const errors = {};
        const phoneFieldConfig = [
            { key: 'customerNumber', label: 'Customer Number' },
            { key: 'serviceAdvisorNumber', label: 'Service Advisor Number' },
            { key: 'serviceManagerNumber', label: 'Service Manager Number' },
            { key: 'customerCareManagerNumber', label: 'Customer Care Manager Number' },
        ];

        phoneFieldConfig.forEach(({ key, label }) => {
            const val = formData[key];
            const validation = validatePhoneNumber(val);
            if (!validation.valid) {
                errors[key] = `${label} ${validation.message}`;
            }
        });

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            toast.error(Object.values(errors)[0]);
            return;
        }

        setFormErrors({});
        await createGroup();
    };

    // Reset Form
    const handleReset = () => {
        setFormData(initialFormState);
        setFormErrors({});
    };

    /**
     * Bundles a flat participant array into one object per unique ROnum.
     * ROnum and GroupStatus appear once; each participant's type + invite
     * statuses are collected into a "participants" array.
     */
    const bundleGroupsByRO = (flatList) => {
        const map = new Map();
        flatList.forEach(({ GroupName, GroupStatus, ParticipantsType, InviteSendingStatus, InviteStatus, InviteSendingDateTime, InviteStatusDateTime }) => {
            if (!map.has(GroupName)) {
                map.set(GroupName, { GroupName, GroupStatus, participants: [] });
            }
            map.get(GroupName).participants.push({ ParticipantsType, InviteSendingStatus, InviteStatus, SendingTime: InviteSendingDateTime, AcceptTime: InviteStatusDateTime });
        });
        return Array.from(map.values());
    };

    const getGroupsList = async () => {
        try {
            const response = await fetch(`${baseUrl}WhatsappGroup/GetGroupParticipantsLists`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const result = await response.json();
            setGroupsList(bundleGroupsByRO(result));
        } catch (error) {
            console.error('Error fetching groups list:', error);
            throw error;
        }
    };

    useEffect(() => {
        const loadSettings = async () => {
            try {
                const settingsData = await handleTpsm();
                const data = settingsData?.[0];
                if (settingsData) {
                    setSettings((prev) => ({
                        ...prev,
                        ...mapApiPayloadToSettings(data),
                    }));
                }
                updateSettingsFromPageData(data);
            } catch (err) {
                console.error('Unable to load settings on page load:', err);
            }
        };

        loadSettings();
        getGroupsList();
    }, []);

    const refreshData = async () => {
        setIsLoading(true);
        setError(null);
        try {
            await getGroupsList();
            toast.success('Groups refreshed');
        } catch (err) {
            console.error('Refresh error:', err);
            toast.error('Failed to refresh groups');
        } finally {
            setIsLoading(false);
        }
    }

    useEffect(() => {
        if (formData.dealerCode && formData.businessNumber) {
            updateSettingsFromPageData();
        }
    }, [formData.dealerCode, formData.businessNumber]);


    return (
        <div className="bg-slate-100 text-slate-800 relative pb-6">
            {/* Top Header */}
            <Navbar nav={[{
                title: 'User Chat',
                link: 'userchat'
            }, { title: 'Group List', link: 'grouplist' }
            ]} />

            <div className="flex flex-col md:flex-row max-w-7xl gap-3 mx-auto">
                {/* Main Container */}
                <main className="w-full md:w-1/2 px-4 pt-4">
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden">

                        {/* Subheading & Title Banner */}
                        <div className="bg-gradient-to-r from-[#001E4A] via-[#002C6C] to-[#004B93] p-3 sm:p-4 text-white relative overflow-hidden">
                            <div className="absolute right-[-20px] top-[-20px] opacity-10 pointer-events-none">
                                <MessageSquare className="w-64 h-64 text-white" />
                            </div>

                            <div className="relative z-10">
                                <h2 className="w-fit text-lg sm:text-xl mx-auto font-extrabold tracking-wide text-white">
                                    Whatsapp Group Automation POC
                                </h2>
                                <div className="w-full flex items-center justify-between gap-2 text-blue-200 my-2 font-medium text-sm sm:text-base">
                                    <div className='w-full flex items-center flex-wrap gap-2'>
                                        <div className='flex items-center gap-2'>
                                            <h3 className="text-sm font-bold">Group Name - </h3>
                                        </div>
                                        <span className='text-[#6bf507] font-bold'>{modalDealerCode || "Dealer Code"}-{formData?.vinNumber || "Vin Number"}-{formData?.roNumber || "RO Number"}</span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    <div className='flex items-center gap-2'>
                                        <label className="w-1/3 md:w-3/4 text-xs font-bold text-white uppercase mb-1.5">Dealer Code</label>
                                        <div className="w-full p-1 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700">{modalDealerCode || 'Not available'}</div>
                                    </div>
                                    <div className='flex items-center gap-2'>
                                        <label className="w-1/3 md:w-fit text-xs font-bold text-white uppercase mb-1.5">WABA Number</label>
                                        <div className="w-full p-1 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700">{modalWabaNumber ? `${modalWabaNumber}` : 'Not available'}</div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Main Form */}
                        <form onSubmit={handleSubmit} className="p-3 sm:p-4 space-y-4">

                            {/* Core Identification Details */}
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2 flex items-center gap-2 border-b border-slate-100 pb-2">
                                    <Smartphone className="w-4 h-4 text-[#002C6C]" />
                                    Whatsapp Group Information
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* a. Vehicle VIN Number */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700  mb-1.5">
                                            Vehicle VIN Number <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                name="vinNumber"
                                                value={formData.vinNumber}
                                                onChange={handleFormChange}
                                                required
                                                maxLength={17}
                                                placeholder="e.g. MALC241CB123456"
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#002C6C] focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium uppercase"
                                            />
                                        </div>
                                    </div>

                                    {/* c. RO Number */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700  mb-1.5">
                                            RO Number <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                type="text"
                                                name="roNumber"
                                                value={formData.roNumber}
                                                onChange={handleFormChange}
                                                required
                                                maxLength={15}
                                                placeholder="e.g. RO-88392"
                                                className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#002C6C] focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium uppercase"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Whatsapp & Business Contact Details */}
                            <div>
                                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4 flex items-center gap-2 border-b border-slate-100 pb-2">
                                    <Phone className="w-4 h-4 text-[#002C6C]" />
                                    Stakeholder Mobile Numbers
                                </h4>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* f. Customer Number */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700  mb-1.5">
                                            Customer Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="customerNumber"
                                            value={formData.customerNumber}
                                            onChange={handleFormChange}
                                            required
                                            maxLength={12}
                                            placeholder="919800011111"
                                            className={`w-full px-3.5 py-2.5 bg-slate-50 border ${formErrors.customerNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#002C6C]'} rounded-lg focus:ring-2 focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium`}
                                        />
                                        {formErrors.customerNumber && (
                                            <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.customerNumber}</p>
                                        )}
                                    </div>

                                    {/* g. Service Advisor Number */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700  mb-1.5">
                                            Service Advisor Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="serviceAdvisorNumber"
                                            value={formData.serviceAdvisorNumber}
                                            onChange={handleFormChange}
                                            required
                                            maxLength={12}
                                            placeholder="919800022222"
                                            className={`w-full px-3.5 py-2.5 bg-slate-50 border ${formErrors.serviceAdvisorNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#002C6C]'} rounded-lg focus:ring-2 focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium`}
                                        />
                                        {formErrors.serviceAdvisorNumber && (
                                            <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.serviceAdvisorNumber}</p>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Stakeholder Contacts */}
                            <div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {/* h. Service Manager Number */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700  mb-1.5">
                                            Service Manager Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="serviceManagerNumber"
                                            value={formData.serviceManagerNumber}
                                            onChange={handleFormChange}
                                            required
                                            maxLength={12}
                                            placeholder="919800033333"
                                            className={`w-full px-3.5 py-2.5 bg-slate-50 border ${formErrors.serviceManagerNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#002C6C]'} rounded-lg focus:ring-2 focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium`}
                                        />
                                        {formErrors.serviceManagerNumber && (
                                            <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.serviceManagerNumber}</p>
                                        )}
                                    </div>

                                    {/* i. Customer Care Manager Number */}
                                    <div>
                                        <label className="block text-xs font-bold text-slate-700  mb-1.5">
                                            Customer Care Manager Number <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="text"
                                            name="customerCareManagerNumber"
                                            value={formData.customerCareManagerNumber}
                                            onChange={handleFormChange}
                                            required
                                            maxLength={12}
                                            placeholder="919800044444"
                                            className={`w-full px-3.5 py-2.5 bg-slate-50 border ${formErrors.customerCareManagerNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#002C6C]'} rounded-lg focus:ring-2 focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium`}
                                        />
                                        {formErrors.customerCareManagerNumber && (
                                            <p className="mt-1 text-xs text-red-500 font-medium">{formErrors.customerCareManagerNumber}</p>
                                        )}
                                    </div>

                                </div>
                            </div>

                            {/* Action Buttons */}
                            <div className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-3 border-t border-slate-200">
                                <button
                                    type="button"
                                    onClick={handleReset}
                                    className="w-full sm:w-auto px-6 py-2.5 rounded-xl border border-slate-300 text-slate-700 hover:bg-slate-100 font-semibold text-sm flex items-center justify-center gap-2 transition-all cursor-pointer"
                                >
                                    <RotateCcw className="w-4 h-4 text-slate-500" />
                                    Reset
                                </button>

                                <button
                                    type="submit"
                                    disabled={isLoading}
                                    className="w-full sm:w-auto px-8 py-2.5 rounded-xl bg-[#002C6C] hover:bg-[#001E4A] active:bg-[#001535] text-white font-semibold text-sm flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20 hover:shadow-xl transition-all cursor-pointer disabled:opacity-50"
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                            Creating...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-4 h-4" />
                                            Create Group
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    </div>
                </main>
                <div className="w-full md:w-3/4 px-4 pt-4">
                    <div className="flex flex-col justify-center items-end mb-3">
                        <div className="flex gap-3 items-center">
                            {/* Fixed Settings Small Button on Top Right */}
                            <button
                                type="button"
                                onClick={() => {
                                    setTempSettings(settings);
                                    setSettingsErrors({});
                                    setIsSettingsOpen(true);
                                }}
                                className="flex items-center gap-2 !bg-[#002356] hover:bg-white/20 active:bg-white/30 text-white px-3.5 py-1 rounded-lg transition-all duration-150 border border-white/20 text-xs sm:text-sm font-semibold shadow-sm cursor-pointer"
                                title="Configure System Settings"
                            >
                                <Settings className="w-4 h-4 text-blue-200" />
                                <span className="hidden sm:inline">Settings</span>
                            </button>
                            <button className='flex items-center gap-2 !bg-[#002356] hover:bg-white/20 active:bg-white/30 text-white px-3.5 p-2 rounded-lg transition-all duration-150 border border-white/20 text-xs sm:text-sm font-semibold shadow-sm cursor-pointer'
                                type="button"
                                onClick={() => {
                                    refreshData();
                                }}>
                                <RefreshCw className='w-4 h-4' />
                            </button>
                        </div>
                    </div>
                    <div className="bg-white rounded-2xl shadow-xl border border-slate-200/80 overflow-hidden">
                        {/* Groups Table */}
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead className='bg-gradient-to-r from-[#001E4A] via-[#002C6C] to-[#004B93] p-3 sm:p-4 text-white relative overflow-hidden'>
                                    <tr className="border-b border-slate-200">
                                        <th
                                            rowSpan={2}
                                            className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider border-r border-slate-200 align-middle whitespace-nowrap"
                                        >
                                            Group Name
                                        </th>
                                        <th
                                            rowSpan={2}
                                            className="px-4 py-3 text-center text-xs font-bold text-white uppercase tracking-wider border-r border-slate-200 align-middle whitespace-nowrap"
                                        >
                                            Group<br /> Created
                                        </th>
                                        <th
                                            colSpan={4}
                                            className="px-4 py-2 text-center text-xs font-bold text-white uppercase tracking-wider border-r border-slate-200 border-b border-slate-200"
                                        >
                                            Invitation Sent
                                        </th>
                                        <th
                                            colSpan={4}
                                            className="px-4 py-2 text-center text-xs font-bold text-white uppercase tracking-wider"
                                        >
                                            Group Joined
                                        </th>
                                    </tr>
                                    <tr className="bg-slate-50 border-b-2 border-slate-200">
                                        {['Cust', 'SA', 'SM', 'CCM'].map((col) => (
                                            <th
                                                key={`inv-${col}`}
                                                className="px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider border-r border-slate-100 last:border-r border-slate-200"
                                            >
                                                {col}
                                            </th>
                                        ))}
                                        {['Cust', 'SA', 'SM', 'CCM'].map((col, i) => (
                                            <th
                                                key={`join-${col}`}
                                                className={`px-3 py-2 text-center text-xs font-semibold text-slate-500 uppercase tracking-wider ${i < 3 ? 'border-r border-slate-100' : ''}`}
                                            >
                                                {col}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {groupsList.length > 0 ? (
                                        groupsList.map((group) => {
                                            // Ensure participants are in the canonical order
                                            const typeOrder = ['custNo', 'saNo', 'smNo', 'ccmNo'];
                                            const byType = Object.fromEntries(
                                                (group.participants || []).map((p) => [p.ParticipantsType, p])
                                            );
                                            return (
                                                <tr key={group.ROnum} className="hover:bg-slate-50 transition-colors border-b border-slate-100">
                                                    {/* RO Number */}
                                                    <td className="max-w-60 px-4 py-3 font-semibold text-slate-800 border-r border-slate-200 break-words">
                                                        {group?.GroupName}
                                                    </td>
                                                    {/* Group Created */}
                                                    <td className="px-3 py-3 text-center border-r border-slate-200">
                                                        <input
                                                            type="checkbox"
                                                            checked={group.GroupStatus === "Approved"}
                                                            readOnly
                                                            className="w-4 h-4 rounded accent-[#002C6C] cursor-default"
                                                        />
                                                    </td>
                                                    {/* Invitation Sent checkboxes */}
                                                    {typeOrder.map((type, i) => (
                                                        <td key={`inv-${type}`} className={`px-3 py-3 text-center border-r ${i < 3 ? 'border-slate-100' : 'border-slate-200'}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={byType[type]?.InviteSendingStatus === 1}
                                                                readOnly
                                                                title={byType[type]?.SendingTime ? `Sent: ${formatDateTime(byType[type].SendingTime)}` : 'Not sent yet'}
                                                                className="w-4 h-4 rounded accent-[#002C6C] cursor-help"
                                                            />
                                                        </td>
                                                    ))}
                                                    {/* Group Joined checkboxes */}
                                                    {typeOrder.map((type, i) => (
                                                        <td key={`join-${type}`} className={`px-3 py-3 text-center ${i < 3 ? 'border-r border-slate-100' : ''}`}>
                                                            <input
                                                                type="checkbox"
                                                                checked={byType[type]?.InviteStatus === 1}
                                                                readOnly
                                                                title={byType[type]?.AcceptTime ? `Joined: ${formatDateTime(byType[type].AcceptTime)}` : 'Not joined yet'}
                                                                className="w-4 h-4 rounded accent-[#002C6C] cursor-help"
                                                            />
                                                        </td>
                                                    ))}
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={10} className="px-4 py-10 text-center text-slate-400 text-sm italic">
                                                No groups found. Submit the form to create a WhatsApp group.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>


            {/* Config Settings Modal */}
            {isSettingsOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-slate-200 transform transition-all">

                        {/* Modal Header */}
                        <div className="flex justify-between items-center p-5 border-b border-slate-100 bg-slate-50">
                            <div>
                                <h3 className="font-bold text-slate-800 flex items-center gap-2 text-base">
                                    <Settings className="w-5 h-5 text-[#002C6C]" />
                                    System Settings
                                </h3>
                            </div>
                            <button
                                type="button"
                                onClick={() => setIsSettingsOpen(false)}
                                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg hover:bg-slate-200/60 transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        {/* Modal Form */}
                        <form onSubmit={handleSaveSettings} className="p-6 space-y-4">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">Dealer Code</label>
                                    <div className="w-full px-3.5 py-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700">{modalDealerCode || 'Not available'}</div>
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">WABA Number</label>
                                    <div className="w-full px-3.5 py-2.5 bg-slate-100 border border-slate-200 rounded-lg text-sm text-slate-700">{modalWabaNumber ? `${modalWabaNumber}` : 'Not available'}</div>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                                    TPSM Number
                                </label>
                                <input
                                    type="text"
                                    name="tpsmNumber"
                                    value={tempSettings.tpsmNumber}
                                    onChange={handleSettingsChange}
                                    maxLength={12}
                                    placeholder="e.g. 919800055555"
                                    className={`w-full px-3.5 py-2.5 bg-slate-50 border ${settingsErrors.tpsmNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#002C6C]'} rounded-lg focus:ring-2 focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium`}
                                />
                                {settingsErrors.tpsmNumber && (
                                    <p className="mt-1 text-xs text-red-500 font-medium">{settingsErrors.tpsmNumber}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-slate-700 uppercase mb-1.5">
                                    RPSM Number
                                </label>
                                <input
                                    type="text"
                                    name="rpsmNumber"
                                    value={tempSettings.rpsmNumber}
                                    onChange={handleSettingsChange}
                                    maxLength={12}
                                    placeholder="e.g. 919800066666"
                                    className={`w-full px-3.5 py-2.5 bg-slate-50 border ${settingsErrors.rpsmNumber ? 'border-red-500 focus:ring-red-500' : 'border-slate-300 focus:ring-[#002C6C]'} rounded-lg focus:ring-2 focus:bg-white focus:border-transparent outline-none text-sm transition-all font-medium`}
                                />
                                {settingsErrors.rpsmNumber && (
                                    <p className="mt-1 text-xs text-red-500 font-medium">{settingsErrors.rpsmNumber}</p>
                                )}
                            </div>

                            {/* Modal Update Button */}
                            <div className="pt-4 flex justify-end gap-2 border-t border-slate-100 mt-6">
                                <button
                                    type="button"
                                    onClick={() => setIsSettingsOpen(false)}
                                    className="px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="px-6 py-2 text-sm font-semibold bg-[#002C6C] hover:bg-[#001E4A] text-white rounded-lg transition-colors shadow-md"
                                >
                                    Update
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Success Modal Confirmation */}
            {isSuccessModalOpen && submittedData && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
                    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden border border-slate-200">

                        <div className="p-6 text-center border-b border-emerald-100 bg-emerald-50/60">
                            <div className="w-14 h-14 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3 shadow-inner">
                                <CheckCircle2 className="w-8 h-8" />
                            </div>
                            <h3 className="text-xl font-bold text-slate-900">
                                Group Creation Successful!
                            </h3>
                        </div>

                        <div className="p-6 space-y-5">
                            {/* Created Group Name */}
                            <div className="p-4 bg-blue-50/70 border border-blue-200/80 rounded-xl">
                                <p className="text-xs font-bold text-blue-800 uppercase tracking-wider mb-1">
                                    Whatsapp Group Name Created
                                </p>
                                <p className="text-base font-extrabold text-[#002C6C]">
                                    {modalDealerCode}-{submittedData.vinNumber}-{submittedData.roNumber}
                                </p>
                            </div>

                            {/* Sent Invites List */}
                            <div>
                                <p className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-3">
                                    Invite to join the whatsapp group has been Sent to:
                                </p>
                                <div className="space-y-2 text-sm">
                                    <div className="flex justify-between items-center py-2 px-3.5 bg-slate-50 rounded-lg border border-slate-200">
                                        <span className="text-slate-600 font-medium">Customer Number:</span>
                                        <span className="font-bold text-slate-800">{submittedData.customerNumber}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-3.5 bg-slate-50 rounded-lg border border-slate-200">
                                        <span className="text-slate-600 font-medium">Service Advisor Number:</span>
                                        <span className="font-bold text-slate-800">{submittedData.serviceAdvisorNumber}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-3.5 bg-slate-50 rounded-lg border border-slate-200">
                                        <span className="text-slate-600 font-medium">Service Manager Number:</span>
                                        <span className="font-bold text-slate-800">{submittedData.serviceManagerNumber}</span>
                                    </div>
                                    <div className="flex justify-between items-center py-2 px-3.5 bg-slate-50 rounded-lg border border-slate-200">
                                        <span className="text-slate-600 font-medium">Customer Care Manager Number:</span>
                                        <span className="font-bold text-slate-800">{submittedData.customerCareManagerNumber}</span>
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className="p-4 bg-slate-50 border-t border-slate-100 flex justify-end">
                            <button
                                type="button"
                                onClick={() => { setIsSuccessModalOpen(false); handleReset() }}
                                className="px-6 py-2.5 bg-[#002C6C] text-white text-sm font-semibold rounded-xl hover:bg-[#001E4A] transition-colors shadow-md"
                            >
                                Close Window
                            </button>
                        </div>

                    </div>
                </div>
            )}
        </div>
    );
}
